import { Dispatch, MouseEvent, SetStateAction, useEffect, useRef, useState } from "react";
import styles from "./DirectionAnalysis.module.css";
import ContentTitle from "../ContentTitle";
import { ContentList } from "../../../../screens/MapScreen";

import TransportSelector, { TransportMode } from "./TransportSelector";
import BottomButtonBox, { ButtomButtonBoxProps } from "../../BottomButtonBox";
import { MiniButtonType } from "../../MiniButton";
import DataProvider from "../../../../data_provider/DataProvider";
import { Coordinate } from "ol/coordinate";
import { Projection } from "ol/proj";
import { Polygon } from "ol/geom";
import { createPolygonIntersectsFilterData, getCoordinatesFromIsochrone } from "../../../../module/map/analysis";
import { FeatureCollection } from "geojson";

import { useModal } from "../../../modal/Modal";
import ModalButtons from "../../../modal/ModalButtons";
import result from "../../../../dummy/process=storyList";
import { WebViewMessageType } from "../../../../hooks/useWebViewBridge";

export enum Stage {
    INIT = -1,

    STEP1,
    STEP2,
    STEP3,
}

interface DirectionAnalysisProps {
    changeContent: (type: ContentList, reset?: boolean) => void;
    startPoint: number[] | undefined;
    setStartPoint: Dispatch<SetStateAction<number[] | undefined>>;
    mapProj: Projection | undefined;
    mapId: string | null;

    //모든 분석이 끝난 후 실행 함수
    finishAnalysis: (xidList: number[], polygon: Polygon) => void;

    // 이전, 초기화 버튼을 위한 분석 단계 파악
    setAnalysisStage: Dispatch<SetStateAction<number>>;
    setClearByButton: Dispatch<SetStateAction<number>>;

    setDirectionVehicle: Dispatch<SetStateAction<string>>;

    // 25.07.03 | Kim Young Jun | 로딩 상태 설정
    setIsLoading: Dispatch<SetStateAction<boolean>>; // 로딩 상태 설정

    // 25.07.14 | Kim Young Jun | 지도 화면 모달 열기/닫기 함수 추가
    openMapScreenModal?: (modalProps: any) => void; // 지도 화면 모달 열기 함수
    closeMapScreenModal?: () => void; // 지도 화면 모달 닫기 함수

    webViewDirectionContext?: { duration: number, vehicle: string } | null; // 26.01.19 | dslee | WebView 에서 전달된 DirectionAnalysis 설정값
    postMessage?: (message: WebViewMessageType) => void; // 26.01.19 | dslee | WebView WEB -> RN 전송 함수
}

type ResultAnalysis = {
    polygon: Polygon | undefined; // map에 그려줄 polygon객체
    xidList: number[];
};

export type IsochroneFeature = {
    type: "Feature";
    geometry: {
        type: "Polygon";
        coordinates: number[][][];
    };
    properties: {
        bucket: number;
    };
};

/**
 * @author smyun
 * @since 2025-01-03
 * 시간별 이동거리 분석 클릭 시 SideSheet 표현부
 */

const DirectionAnalysis = (props: DirectionAnalysisProps) => {
    
    // 25.07.14 | Kim Young Jun | 지도 화면 모달 열기/닫기 함수 추가
    const { openMapScreenModal, closeMapScreenModal } = props;

    const [vehicle, setVehicle] = useState<TransportMode>(TransportMode.car);
    const [time, setTime] = useState<number>(10); // 초기값 10분
    const [isochroneResult, setIsochroneResult] = useState<IsochroneFeature>();
    const [getfeatureResult, setGetfeatureResult] = useState<FeatureCollection>();

    const [resultAnalysis, setResultAnalysis] = useState<ResultAnalysis>({
        xidList: [],
        polygon: undefined,
    });

    // 2026.01.19 | dslee | 에러 메시지 표시 - WebView 환경이면 RN으로 전송, 아니면 모달 표시
    const showErrorMessage = (message: string) => {
        // WebView 환경에서 RN으로부터 받은 요청인 경우 RN으로 에러 전송
        if (props.webViewDirectionContext && props.postMessage) {
            props.postMessage({ type: 'WEB_VIEW_MESSAGE', payload: message});
            props.postMessage({ type: 'WEB_VIEW_LOADING_STATE', payload: { isLoading: false }});
        } else {
            // 일반 웹 환경에서는 모달 표시
            openMapScreenModal &&
                openMapScreenModal({
                    title: "안내",
                    children: message,
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () => closeMapScreenModal && closeMapScreenModal(),
                                },
                            ]}
                        />
                    ),
                });
        }
    };

    // console.log(`DAstates : ${vehicle + time + JSON.stringify(isochroneResult?.geometry.coordinates.length) + JSON.stringify(getfeatureResult) + JSON.stringify(resultAnalysis)}`);

    /* getIntersectFeatureData.result 구조
        const result = {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    id: "kd330e1efea99b12848d9d91a8060aa50.9",
                    properties: {
                        주소: "서울특별시 강남구 대치동897-26",
                        건물명: "",
                        "연락처1(소유자)": "",
                        "연락처2(관리인)": "생활맥주사장님(1층)010-2240-8412",
                        연락처3: "",
                        연락처4: "",
                        구분: "임차인",
                        전화번호: "010-2240-8412",
                    },
                    geometry: { type: "Point", coordinates: [960370.968757, 1944946.677364] },
                },               
                ...                        
            ],
        };
    */

    /** useEffect -------------------------------------------------------------------------- */
    useEffect(() => {
        // 2
        //console.log("useEffect isochroneResult: ", isochroneResult);
        if (isochroneResult && isochroneResult?.geometry) {
            // isochronedata에 geometry안에 좌표값이 있는지 확인
            if (!isochroneResult?.geometry.coordinates) {
                showErrorMessage("데이터에 오류가 있습니다. 다시 시도해주세요");
                return;
            }

            const mapProj = props.mapProj!; // 좌표계
            // graphHopper isochrone Data를 ogc에 맞게 가공(1 xyz -> xy & 2 lat,lon -> lon,lat & 3 좌표 변경)
            const coords = getCoordinatesFromIsochrone(isochroneResult, mapProj); // map좌표계로 변경

            fetchFeatureData(coords);
        }
    }, [isochroneResult]);

    useEffect(() => {
        // 4
        // 가져온 결과 중 xid 추출
        if (getfeatureResult && getfeatureResult.features.length > 0) {
            let xids: number[] = [];
            xids = getfeatureResult.features.map((feature) => {
                const fid = feature.id as string;
                const idx = fid!.indexOf(`.`);
                return Number(fid.substring(idx + 1));
            });

            props.setAnalysisStage(Stage.STEP2); // 분석단계 ISOCHRONE
            setResultAnalysis((prev) => ({
                ...prev,
                xidList: xids,
            }));
        } else if (getfeatureResult && getfeatureResult?.features.length == 0) {
            props.setAnalysisStage(Stage.STEP2); // 분석단계 ISOCHRONE

            //포함된 장소가 없어도 폴리곤 그리기위해 제거함
            //props.setClearByButton(0);

            //조회된 장소가 없는 경우는 빈 리스트를 보여주기위해 -1 리턴
            setResultAnalysis((prev) => ({
                ...prev,
                xidList: [-1],
            }));
        }
    }, [getfeatureResult]);

    useEffect(() => {
        // 5
        // MapScreen -> sideSheet, map으로 보낼 xidList와 Polygon 세팅
        if (resultAnalysis.polygon && resultAnalysis.xidList.length > 0) {
            props.finishAnalysis(resultAnalysis?.xidList, resultAnalysis.polygon);
        }
    }, [resultAnalysis]);

    useEffect(() => {
        props.setDirectionVehicle(vehicle);
    }, [vehicle]);

    // 2026.01.19 | dslee | WebView 설정값이 들어오면 내부 state 동기화
    useEffect(() => {
        if (props.webViewDirectionContext) {
            setVehicle(props.webViewDirectionContext.vehicle as TransportMode);
            setTime(props.webViewDirectionContext.duration);
        }
    }, [props.webViewDirectionContext]);

    // 2026.01.19 | dslee | webViewDirectionContext 있는경우 자동 시작
    useEffect(() => {
        if (props.webViewDirectionContext && props.startPoint) {
            fetchIsochrone();
        }
    }, [props.webViewDirectionContext]);

    /** 함수 -------------------------------------------------------------------------- */
    // 이동 시간 변경
    const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTime(Number(event.target.value));
    };
    const handleStepClick = (time: number) => {
        setTime(time);
    };

    /**
     * isochrone polygon과 intersect되는 feature list 검색용 API
     * @param coords isochrone polygon의 좌표배열
     */
    const fetchFeatureData = async (coords: Coordinate[][]) => {
        // 3
        const polygon = new Polygon(coords); //coords: mapProj 좌표계
        // polygon.getType() -> Polygon(폴리곤 정상 생성 확인 완료)

        const mapProj = props.mapProj?.getCode();
        const mapid = props.mapId;

        // setResultAnalysis 에 폴리곤 셋
        setResultAnalysis({
            xidList: [],
            polygon,
        });

        //console.log("fetchFeatureData")


        if (mapProj && mapid) {
            const filter = createPolygonIntersectsFilterData(polygon, mapProj);

            // 데이터 프로바이더로 getfeature
            try {
                const getIntersectFeatureData = await DataProvider.GetFeatureList({ mapid }, { ogcfilter: { filter: filter } });
                if (!getIntersectFeatureData.flag) {
                    // FIXME 결과 메시지 statues 확인
                    if (getIntersectFeatureData.status === 204) showErrorMessage("분석 결과가 없습니다."); //SC_NO_CONTENT(graphhopper 검색결과 Polygon 없을 시)
                    if (getIntersectFeatureData.status === 500) showErrorMessage("분석에 실패했습니다."); //SC_INTERNAL_SERVER_ERROR
                    return;
                }
                const result = getIntersectFeatureData.result;
                //console.log("getIntersectFeatureData.result: ", result)
                if (result.features.length > 0) {
                    setGetfeatureResult(result); // 결과 set
                } else {
                    setGetfeatureResult(result); // 결과 set
                    //showModal("이동 가능한 거리 내에 포함되는 장소가 없습니다.");

                    //포함된 장소가 없어도 폴리곤 그리기위해 제거함
                    //props.setClearByButton(0);
                    //props.setStartPoint(undefined);
                }
            } catch (error) {
                showErrorMessage("Isochrone 내부 feature 목록 데이터를 가져오는 데 실패했습니다.");
            }
        }
    };

    /** DATA API ----------------------------------------------------------------------------- */
    const fetchIsochrone = async () => {
        // 1
        try {
            // 25.07.03 | Kim Young Jun | 로딩 상태 시작
            props.setIsLoading && props.setIsLoading(true);

            // 26.01.19 | dslee | RN 으로 로딩 상태 전송
            props.postMessage?.({ 
                type: 'WEB_VIEW_LOADING_STATE', 
                payload: { isLoading: true } 
            });
            
            const getIsochroneData = await DataProvider.GetIsochroneData({
                point: props.startPoint!,
                time_limit: time * 60,
                vehicle: vehicle,
            }); // : polygons[0]

            if (!getIsochroneData.flag) {
                if (getIsochroneData.status === 204) showErrorMessage("분석 결과가 없습니다."); //SC_NO_CONTENT(graphhopper 검색결과 Polygon 없을 시)
                if (getIsochroneData.status === 500) showErrorMessage("분석에 실패했습니다."); //SC_INTERNAL_SERVER_ERROR
                // 25.07.14 | Kim Young Jun | 로딩 종료
                props.setIsLoading && props.setIsLoading(false);
                return;
            }

            const result = getIsochroneData.result;
            //console.log("getIsochroneData.result: ", result);
            setIsochroneResult(result); // 결과 set

            // 25.07.03 | Kim Young Jun | 로딩 상태 500ms 후 종료
            setTimeout(() => {
                props.setIsLoading && props.setIsLoading(false);

                // 26.01.19 | dslee | RN 으로 로딩 상태 전송
                props.postMessage?.({ 
                    type: 'WEB_VIEW_LOADING_STATE', 
                    payload: { isLoading: false } 
                });
                
                // 26.01.21 | dslee | RN 으로 분석 성공 전송
                props.postMessage?.({ 
                    type: 'DIRECTION_ANALYSIS_COMPLETED', 
                    payload: { isCompleted: true } 
                });
            }, 500);

        } catch (error) {
            showErrorMessage("Isochrone 데이터를 가져오는 데 실패했습니다.");
        }
    };

    // 하단 버튼
    const buttonOptions: ButtomButtonBoxProps = {
        left: {
            label: "취소",
            type: MiniButtonType.GRAY,
            onClick: () => {
                props.changeContent(ContentList.List, true);
            },
        },
        right: {
            label: "분석",
            onClick: async () => {
                // 시작점이 있을 때만 분석 시작
                if (!props.startPoint) return;
                
                // 즉시 List 뷰로 변경 (AccessibilityAnalysis와 동일한 로직)
                // props.changeContent(ContentList.List);
                
                try {
                    //console.log("[시간별 이동거리 분석 시작]");
                    await fetchIsochrone(); // TODO 데이터가 많아서 분석이 길어질 경우 프로그레싱 시각적 효과 필요
                } catch (error) {
                    showErrorMessage("분석 중 오류 발생");
                }
            },
            type: props.startPoint ? MiniButtonType.BLUE : MiniButtonType.DISABLED,
        },
    };

    return (
        <>
            <div className={`${styles.wrapper} undraggable`}>
                <ContentTitle title={"시간별 이동 가능 거리"} />
                <div className={styles.container}>
                    <div className={styles.top}>
                        <div className={styles.content}>
                            <div className={styles.title}>{"분석 범위 설정"}</div>
                            <div className={styles.desc}>{"이동수단 및 이동 시간을 선택하고 지도 화면에서 분석을 시작할 지점을 선택해주세요."}</div>
                        </div>
                    </div>
                    <div className={styles.middle}>
                        <div className={styles.content}>
                            <span className={styles.title}>{"이동 수단"}</span>
                            <TransportSelector selectedMode={vehicle} setSelectedMode={setVehicle}></TransportSelector>
                        </div>
                        <div className={styles.content}>
                            <span className={styles.title}>{"이동 시간"}</span>
                            {/* slider controls */}
                            <div className={styles.sliderWrapper}>
                                <div className={styles.track}>
                                    {/* 파란색 채움 영역 */}
                                    <div className={styles.fill} style={{ width: `${((time - 5) / 25) * 100}%` }}></div>
                                    {/* 시작/종료 시간 레이블 */}
                                    <div className={`${styles.step} ${time === 5 ? styles.active : ""}`} onClick={() => handleStepClick(5)}>
                                        <span className={styles.stepLabel}>5분</span>
                                    </div>
                                    {[10, 15, 20, 25].map((time) => (
                                        <div
                                            key={time}
                                            className={`${styles.step} ${time === time ? styles.active : ""}`}
                                            onClick={() => handleStepClick(time)}
                                        >
                                            <span className={styles.stepLabel}>{time}분</span>
                                        </div>
                                    ))}
                                    <div className={`${styles.step} ${time === 30 ? styles.active : ""}`} onClick={() => handleStepClick(30)}>
                                        <span className={styles.stepLabel}>30분</span>
                                    </div>
                                </div>
                                {/* 핸들 */}
                                <div
                                    className={styles.handle}
                                    style={{
                                        left: `${((time - 5) / 25) * 100}%`,
                                    }}
                                ></div>
                                {/* <input>을 사용하여 드래그 가능 */}
                                <input
                                    className={styles.rangeInput}
                                    type="range"
                                    min="5"
                                    max="30"
                                    step="5"
                                    value={time}
                                    onChange={handleSliderChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.bottom}>
                    <BottomButtonBox {...buttonOptions} />
                </div>
            </div>
        </>
    );
};

export default DirectionAnalysis;
